/**
 * Admin auth boundary.
 *
 * Blocks the app shell until a valid Supabase session exists and the main
 * Worker confirms `users.is_admin = true` for that account.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-20-v1
 */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
    AlertTriangle,
    KeyRound,
    LoaderCircle,
    LockKeyhole,
    LogOut,
    ShieldCheck
} from 'lucide-react';
import { useAdminAuthStore } from '../lib/auth';
import { getEnvironment } from '../lib/env';
import type { Tone } from '../lib/types';

export function AdminAuthGate({ children }: { children: ReactNode }) {
    const status = useAdminAuthStore((state) => state.status);
    const init = useAdminAuthStore((state) => state.init);

    useEffect(() => {
        void init();
    }, [init]);

    if (status === 'authenticated') {
        return <>{children}</>;
    }

    if (status === 'loading') {
        return (
            <AuthFrame
                icon={<LoaderCircle size={18} className="spin" />}
                kicker="Admin Auth"
                title="Checking admin access"
                subtitle="Validating the Supabase session and confirming this account is marked admin in the existing users table."
                tone="info"
            />
        );
    }

    if (status === 'forbidden') {
        return <ForbiddenScreen />;
    }

    return <SignInScreen />;
}

function SignInScreen() {
    const signInWithPassword = useAdminAuthStore((state) => state.signInWithPassword);
    const signInWithGoogle = useAdminAuthStore((state) => state.signInWithGoogle);
    const refresh = useAdminAuthStore((state) => state.refresh);
    const error = useAdminAuthStore((state) => state.error);
    const emailHint = useAdminAuthStore((state) => state.emailHint);
    const status = useAdminAuthStore((state) => state.status);

    const [email, setEmail] = useState(emailHint ?? '');
    const [password, setPassword] = useState('');

    useEffect(() => {
        if (emailHint) setEmail(emailHint);
    }, [emailHint]);

    const busy = status === 'loading';

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        await signInWithPassword(email, password);
        setPassword('');
    };

    return (
        <AuthFrame
            icon={<ShieldCheck size={18} />}
            kicker="Admin Auth"
            title="Sign in to Producer Dashboard Admin"
            subtitle="This site uses the same Supabase project as the main app, but access is granted only when your existing users-table row is marked admin."
            tone="info"
        >
            <form className="auth-form" onSubmit={handleSubmit}>
                <div className="form-field">
                    <label className="form-label" htmlFor="admin-email">
                        Email
                    </label>
                    <input
                        id="admin-email"
                        className="form-input"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@producerdashboard.app"
                    />
                </div>

                <div className="form-field">
                    <label className="form-label" htmlFor="admin-password">
                        Password
                    </label>
                    <input
                        id="admin-password"
                        className="form-input"
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                    />
                </div>

                {error && (
                    <div className="auth-banner" data-kind="danger">
                        <AlertTriangle size={14} />
                        <span>{error}</span>
                    </div>
                )}

                <div className="auth-actions">
                    <button className="btn btn-primary" type="submit" disabled={busy}>
                        {busy ? 'Signing in…' : 'Sign in'}
                    </button>
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => void signInWithGoogle()}
                        disabled={busy}
                    >
                        Continue with Google
                    </button>
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => void refresh()}
                        disabled={busy}
                    >
                        Retry session
                    </button>
                </div>
            </form>

            <ul className="auth-notes">
                <li>
                    Email/password sign-in stays on the admin origin and does not need a redirect.
                </li>
                <li>
                    Google sign-in returns here because the admin app sets `redirectTo` to its own
                    origin.
                </li>
                <li>
                    Even after sign-in, the main Worker still rejects access unless `users.is_admin
                    = true`.
                </li>
            </ul>
        </AuthFrame>
    );
}

function ForbiddenScreen() {
    const emailHint = useAdminAuthStore((state) => state.emailHint);
    const error = useAdminAuthStore((state) => state.error);
    const signOut = useAdminAuthStore((state) => state.signOut);
    const refresh = useAdminAuthStore((state) => state.refresh);

    return (
        <AuthFrame
            icon={<LockKeyhole size={18} />}
            kicker="Access Denied"
            title="Authenticated, but not an admin"
            subtitle={
                emailHint
                    ? `${emailHint} has a valid Supabase session, but the existing users row is not marked admin.`
                    : 'This account has a valid Supabase session, but the existing users row is not marked admin.'
            }
            tone="warning"
        >
            {error && (
                <div className="auth-banner" data-kind="warning">
                    <AlertTriangle size={14} />
                    <span>{error}</span>
                </div>
            )}

            <div className="auth-actions">
                <button className="btn btn-ghost" type="button" onClick={() => void refresh()}>
                    Retry access check
                </button>
                <button className="btn btn-danger" type="button" onClick={() => void signOut()}>
                    <LogOut size={14} /> Sign out
                </button>
            </div>
        </AuthFrame>
    );
}

function AuthFrame({
    icon,
    kicker,
    title,
    subtitle,
    tone,
    children
}: {
    icon: ReactNode;
    kicker: string;
    title: string;
    subtitle: string;
    tone: Tone;
    children?: ReactNode;
}) {
    const environment = getEnvironment();
    const environmentTone: Tone =
        environment === 'production' ? 'success' : environment === 'dev' ? 'warning' : 'purple';
    const environmentLabel =
        environment === 'production'
            ? 'Production'
            : environment === 'dev'
              ? 'Dev Worker'
              : 'Local';
    const railItems: Array<{ icon: ReactNode; title: string; body: string; tone: Tone }> = [
        {
            icon: <KeyRound size={14} />,
            title: 'Shared session',
            body: 'Supabase authenticates on the admin origin so the login flow stays within this surface.',
            tone: 'info'
        },
        {
            icon: <ShieldCheck size={14} />,
            title: 'Server-verified access',
            body: 'The main worker still confirms the existing users row is marked admin before the shell unlocks.',
            tone: 'success'
        },
        {
            icon: <LockKeyhole size={14} />,
            title: 'Ops-grade controls',
            body: 'Admin affordances stay role-gated and every real mutation is expected to land in the audit trail.',
            tone: 'purple'
        }
    ];

    return (
        <div className="auth-screen">
            <div className="auth-layout">
                <aside className="auth-rail">
                    <div className="auth-brand">
                        <div className="auth-brand-lockup">
                            <div className="auth-brand-logo">PD</div>
                            <div className="auth-brand-copy">
                                <span className="auth-brand-title">Producer Dashboard</span>
                                <span className="auth-brand-kicker">Admin</span>
                            </div>
                        </div>

                        <div className="topbar-env" data-env={environment}>
                            <span className="topbar-env-dot" />
                            {environmentLabel}
                        </div>
                    </div>

                    <div className="auth-rail-panel">
                        <div className="auth-rail-chip-row">
                            <span className="chip" data-tone="info">
                                <span className="dot" /> Shared Supabase auth
                            </span>
                            <span className="chip" data-tone={environmentTone}>
                                <span className="dot" /> {environmentLabel}
                            </span>
                        </div>

                        <h2 className="auth-rail-title">Same system. Tighter gate.</h2>
                        <p className="auth-rail-copy">
                            Authentication happens through the shared Supabase project, but entry to
                            admin stays blocked until the main worker verifies your existing users
                            row is allowed in.
                        </p>
                    </div>

                    <div className="auth-rail-grid">
                        {railItems.map((item) => (
                            <div key={item.title} className="auth-rail-card">
                                <div className="auth-rail-card-head">
                                    <span className="auth-rail-card-icon" data-tone={item.tone}>
                                        {item.icon}
                                    </span>
                                    <span>{item.title}</span>
                                </div>
                                <p>{item.body}</p>
                            </div>
                        ))}
                    </div>
                </aside>

                <section className="auth-stage">
                    <div className="auth-stage-topbar">
                        <div className="auth-stage-path">
                            <span className="auth-stage-domain">admin.producerdashboard.app</span>
                            <span className="auth-stage-separator">/</span>
                            <span className="auth-stage-area">secure access</span>
                        </div>

                        <span className="chip" data-tone={tone}>
                            <span className="dot" /> {kicker}
                        </span>
                    </div>

                    <div className="auth-card" data-tone={tone}>
                        <div className="auth-icon" data-tone={tone}>
                            {icon}
                        </div>
                        <div className="auth-kicker">{kicker}</div>
                        <h1 className="auth-title">{title}</h1>
                        <p className="auth-subtitle">{subtitle}</p>
                        {children}
                    </div>
                </section>
            </div>
        </div>
    );
}
